import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import {
  applySort,
  pickSort,
  readSort,
  type SortOption,
  type SortSearch,
  type SortState,
  sortToSearch,
} from '@/lib/sorting';

/** The pair of search params one control reads and writes. */
export interface SortSearchKeys {
  sort: string;
  dir: string;
}

const DEFAULT_KEYS: SortSearchKeys = { sort: 'sort', dir: 'dir' };

/**
 * What one control's pair of search keys means, out of the page's raw search
 * object.
 *
 * Pulled out of the hook below and exported so a test can pin the case that
 * motivated `keys` in the first place: two controls on one page, each with its
 * own pair, reading only their own choice and never each other's.
 */
export function readNamespacedSearch(
  rawSearch: Record<string, unknown>,
  keys: SortSearchKeys,
): SortSearch {
  return {
    sort: typeof rawSearch[keys.sort] === 'string' ? (rawSearch[keys.sort] as string) : undefined,
    dir:
      rawSearch[keys.dir] === 'asc' || rawSearch[keys.dir] === 'desc'
        ? (rawSearch[keys.dir] as SortSearch['dir'])
        : undefined,
  };
}

/**
 * The chosen order, read from the URL and written back to it.
 *
 * A sort is not a place, so it replaces the history entry rather than stacking
 * one per click: Back should leave the page, not walk the reader out through six
 * orders. This is the same decision, for the same reason, that the filters on
 * `/tasks` already make.
 *
 * The route has to keep `sort` and `dir` in its `validateSearch`, or the router
 * strips them on the first navigation and the control appears to do nothing. See
 * `sortSearchOf` in `router.tsx`.
 *
 * `keys` names the search params, defaulting to `sort`/`dir`. A page with more
 * than one sortable list (`/project/:name`'s steps and its jobs list) has to
 * give each `useSort` call its own pair, or the two controls would read and
 * write the same URL params and each would clobber the other's choice.
 */
export function useSort<T>(
  options: readonly SortOption<T>[],
  id: (row: T) => string,
  keys: SortSearchKeys = DEFAULT_KEYS,
): {
  state: SortState;
  /** The keys as given, so a page declares them once and the control reads them. */
  options: readonly SortOption<T>[];
  /** Pick a key. The key already in use reverses; see `pickSort`. */
  choose: (value: string) => void;
  /** A new array in the chosen order. Safe to call per group. */
  sort: (rows: readonly T[]) => T[];
} {
  const rawSearch = useSearch({ strict: false }) as Record<string, unknown>;
  const search = readNamespacedSearch(rawSearch, keys);
  const navigate = useNavigate();
  const state = readSort(options, search);

  const choose = useCallback(
    (value: string) => {
      const next = pickSort(options, state, value);
      const patch = sortToSearch(options, next);
      void navigate({
        to: '.',
        params: true,
        search: (previous: Record<string, unknown>) => ({
          ...previous,
          [keys.sort]: patch.sort,
          [keys.dir]: patch.dir,
        }),
        replace: true,
      });
    },
    [navigate, options, state, keys.sort, keys.dir],
  );

  const sort = useCallback(
    (rows: readonly T[]) => applySort(rows, options, state, id),
    [id, options, state],
  );

  return useMemo(() => ({ state, options, choose, sort }), [state, options, choose, sort]);
}
